/**
 * @file index.tsx
 *
 * @fileoverview Renders featured blogs with intro text, and
 * intro second text.
 */
import dynamic from 'next/dynamic';
import { BlogPost } from '~/jobiqo/models/blog';
import Link from '~/jobiqo/components/Link';
import { LinkButton } from '~/vcl/atoms/01-forms/01-button/02-link-button';
import { FormattedMessage } from 'react-intl';
import { AddIcon } from '~/vcl/icons/Add';
import Main from '~/vcl/atoms/06-layout/09-main';
import { Box } from '~/vcl/atoms/07-box';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';
import { FlexItems } from '~/vcl/atoms/06-layout/03-flex';
import { FlexItem } from '~/vcl/atoms/06-layout/03-flex/02-flex-item';
import { Text } from '~/vcl/atoms/13-text/03-text';
import { Grid } from '~/vcl/atoms/06-layout/04-grid';
import { HideMobile } from '~/vcl/atoms/06-layout/02-visibility/02-hide-mobile';
import NextLink from 'next/link';

const BlogTeaserCell = dynamic(
  import('~/jobiqo/components/BlogSearch/components/BlogTeaserCell'),
);

export type FeaturedBlogsParagraph = {
  background?: string;
  blogs?: {
    items: BlogPost[];
  }[];
  introText?: string;
  introTextSecondary?: string;
  spacing?: number;
};

interface LandingPageFeaturedJobsProps {
  /**
   * Top intro text for display on the landing page row.
   */
  rowFeaturedBlogsInfo: FeaturedBlogsParagraph;
  /**
   * The size for the container.
   */
  maxContainerSize?: 'small' | 'medium' | 'large' | 'xLarge';
}

const ShowAllBlogsButton = () => (
  <Link href="/blog" prefetch={false} asChild>
    <LinkButton linkElement={NextLink} buttonStyle="secondary" raised={false}>
      <AddIcon title="Show more" />
      <span>
        <FormattedMessage
          id="landing_blogs_link"
          defaultMessage="See all articles"
        />
      </span>
    </LinkButton>
  </Link>
);

export default function LandingPageFeaturedBlogs({
  rowFeaturedBlogsInfo: {
    background,
    spacing,
    blogs,
    introText,
    introTextSecondary,
  },
  maxContainerSize,
}: LandingPageFeaturedJobsProps) {
  return (
    <Box
      boxShadow="none"
      borderRadius="0px"
      margin={0}
      padding={0}
      backgroundColorOverride={
        background ? `var(--color-${background})` : 'transparent'
      }
    >
      <Main {...(maxContainerSize ? { size: maxContainerSize } : null)}>
        <Spacing
          paddingTop={spacing ?? 11}
          paddingBottom={spacing ?? 12}
          margin={0}
        >
          <FlexItems justifyContent="space-between" alignItems="center">
            <FlexItem>
              {introText && (
                <>
                  <Text
                    className="jd-lp-blog-teasers-intro"
                    color="primary"
                    fontWeight="bold"
                    textTransform="uppercase"
                  >
                    {introText}
                  </Text>
                  <Spacing size="medium" />
                </>
              )}
              {introTextSecondary && (
                <>
                  <Text tag="h2" fontSize={[5, 6]} lineHeight={[5, 6]}>
                    {introTextSecondary}
                  </Text>
                  <Spacing size="large" />
                </>
              )}
            </FlexItem>
            <HideMobile>
              <FlexItem justifySelf="flex-end">
                <ShowAllBlogsButton />
              </FlexItem>
            </HideMobile>
          </FlexItems>
          <Grid className="lp-blog-teasers-grid" columns={[1, 2, 3]}>
            {blogs?.[0].items.map((blogPost: BlogPost) => (
              <BlogTeaserCell blogPost={blogPost} key={blogPost.id} />
            ))}
          </Grid>
        </Spacing>
      </Main>
    </Box>
  );
}
export { LandingPageFeaturedBlogs };
